import parse from 'html-react-parser';
import React, {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { MotionDiv } from '@/components/Common/MotionDiv/MotionDiv';
import AMP_KEYS from '@/configs/amp-track-keys';
import useHeySynclyStore from '@/stores/HeySynclyStore';
import { track } from '@/utils/track/track';

import { formatMessageWithCitations } from '../../lib/citation';
import {
  HEY_SYNCLY_MESSAGE_TYPE,
  type HEY_SYNCLY_STATUS,
  type HeySynclyAnswer,
  type HeySynclyAnswerStudioItem,
  type HeySynclyChatMessage,
} from '../../model';
import { QuestionMessage } from '../question-message/question-message';
import { AnalyzedFeedbackCountDisplay } from './analyzed-feedback-display';
import { CollapsibleReason } from './collapsible-reason';
import { MessageActions } from './message-actions';
import { MessageTextStyle } from './message-text-style';
import { StudioItemPreview } from './studio-item-preview';
import { TempAnswerDisplay } from './temp-answer-display';
import { useParseOptions } from './use-parse-options';

type Props = {
  chat_id: string;
  messages: HeySynclyChatMessage[];
  status: HEY_SYNCLY_STATUS;
  answer: HeySynclyAnswer | null;
  onStudioItemClick: (item: HeySynclyAnswerStudioItem) => void;
  additionalAction?: ({
    chat_id,
    question,
  }: {
    chat_id: string;
    question?: string;
  }) => React.ReactNode;
  container: HTMLDivElement | React.RefObject<HTMLDivElement | null> | null;
  hideQuestion?: boolean;
  hideReason?: boolean;
  hideFeedbackCount?: boolean;
  hideVote?: boolean;
  report_alias?: string;
};

export const HeySynclyMessageDisplay = (props: Props) => {
  const [hasChatHistoryInThisSession, setHasChatHistoryInThisSession] =
    useState(false);
  const [showTopBlur, setShowTopBlur] = useState(false);
  const activeAnswerSessionKeyRef = useRef<string | null>(null);
  const hasUserInterruptedAutoScrollRef = useRef(false);
  const isProgrammaticScrollRef = useRef(false);
  const pendingAutoScrollFrameRef = useRef<number | null>(null);
  const previousViewportScrollTopRef = useRef(0);
  const selectedStudioItemDetail = useHeySynclyStore(
    (state) => state.studioItemDetail,
  );
  const { messages, chat_id, answer, report_alias, container } = props;
  const currentAnswerSessionKey = answer ? `${answer.chat_alias}:${messages.length}` : null;
  const scrollBottomOffset =
    answer?.reason && !answer.answer ? 96 : 0;
  const getScrollViewport = useCallback(() => {
    if (container && 'current' in container) {
      return container.current?.querySelector<HTMLDivElement>(
        '[data-slot="scroll-area-viewport"]',
      );
    }

    if (container instanceof HTMLDivElement) {
      return container.querySelector<HTMLDivElement>(
        '[data-slot="scroll-area-viewport"]',
      );
    }

    return document.querySelector<HTMLDivElement>(
      '[data-slot="scroll-area-viewport"]',
    );
  }, [container]);

  const lastAnswerMinHeightClass = hasChatHistoryInThisSession
    ? 'min-h-[calc(100vh-354px)]'
    : '';
  const { options: parseOptions } = useParseOptions({
    chatAlias: chat_id,
    clickEnabled: true,
    reportAlias: report_alias,
  });

  const scrollToBottom = useCallback(() => {
    const viewport = getScrollViewport();

    if (!viewport) return;

    viewport.scrollTo({
      top: Math.max(
        viewport.scrollHeight - viewport.clientHeight - scrollBottomOffset,
        0,
      ),
      behavior: 'auto',
    });
  }, [getScrollViewport, scrollBottomOffset]);

  useLayoutEffect(() => {
    if (!currentAnswerSessionKey) {
      activeAnswerSessionKeyRef.current = null;
      hasUserInterruptedAutoScrollRef.current = false;
      previousViewportScrollTopRef.current = 0;
      return;
    }

    if (activeAnswerSessionKeyRef.current !== currentAnswerSessionKey) {
      activeAnswerSessionKeyRef.current = currentAnswerSessionKey;
      hasUserInterruptedAutoScrollRef.current = false;
      previousViewportScrollTopRef.current = 0;
    }
  }, [currentAnswerSessionKey]);

  useLayoutEffect(() => {
    const viewport = getScrollViewport();

    if (!viewport) {
      setShowTopBlur(false);
      return;
    }

    const interruptAutoScroll = () => {
      hasUserInterruptedAutoScrollRef.current = true;
      isProgrammaticScrollRef.current = false;
      if (pendingAutoScrollFrameRef.current !== null) {
        cancelAnimationFrame(pendingAutoScrollFrameRef.current);
        pendingAutoScrollFrameRef.current = null;
      }
    };

    const syncTopBlur = () => {
      setShowTopBlur(viewport.scrollTop > 0);

      if (!answer || isProgrammaticScrollRef.current) {
        previousViewportScrollTopRef.current = viewport.scrollTop;
        return;
      }

      const threshold = 24;
      const isAtBottom =
        viewport.scrollTop + viewport.clientHeight >=
        viewport.scrollHeight - threshold;
      const hasScrolledUp =
        viewport.scrollTop < previousViewportScrollTopRef.current - 4;

      if (hasScrolledUp || !isAtBottom) {
        interruptAutoScroll();
      }

      previousViewportScrollTopRef.current = viewport.scrollTop;
    };
    const handleWheel = (event: WheelEvent) => {
      if (!answer) {
        return;
      }

      if (event.deltaY < 0) {
        interruptAutoScroll();
      }
    };
    const handleTouchMove = () => {
      if (!answer) {
        return;
      }

      interruptAutoScroll();
    };
    const handlePointerDown = () => {
      if (!answer) {
        return;
      }

      interruptAutoScroll();
    };

    previousViewportScrollTopRef.current = viewport.scrollTop;
    syncTopBlur();
    viewport.addEventListener('scroll', syncTopBlur, { passive: true });
    viewport.addEventListener('wheel', handleWheel, { passive: true });
    viewport.addEventListener('touchmove', handleTouchMove, { passive: true });
    viewport.addEventListener('pointerdown', handlePointerDown, {
      passive: true,
    });

    return () => {
      viewport.removeEventListener('scroll', syncTopBlur);
      viewport.removeEventListener('wheel', handleWheel);
      viewport.removeEventListener('touchmove', handleTouchMove);
      viewport.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [answer, getScrollViewport]);

  useLayoutEffect(() => {
    if (!answer || hasUserInterruptedAutoScrollRef.current) return;

    setHasChatHistoryInThisSession(true);
    pendingAutoScrollFrameRef.current = requestAnimationFrame(() => {
      isProgrammaticScrollRef.current = true;
      scrollToBottom();
      requestAnimationFrame(() => {
        isProgrammaticScrollRef.current = false;
      });
    });
    return () => {
      if (pendingAutoScrollFrameRef.current !== null) {
        cancelAnimationFrame(pendingAutoScrollFrameRef.current);
        pendingAutoScrollFrameRef.current = null;
      }
      isProgrammaticScrollRef.current = false;
    };
  }, [answer, messages.length, scrollToBottom]);

  return (
    <div className="flex flex-col items-center">
      <div className="w-full min-w-0 max-w-200">
        <div className="relative flex flex-col w-full min-w-0 gap-5 overflow-hidden">
          {showTopBlur && (
            <div
              aria-hidden="true"
              data-testid="hey-syncly-top-blur"
              className="pointer-events-none absolute inset-x-0 top-0 z-10 h-5 bg-gradient-to-b from-background via-background/80 to-transparent backdrop-blur-sm"
            />
          )}
          {messages.map((message, index, arr) => {
            const isLastAnswer =
              index === arr.length - 1 &&
              message.type === HEY_SYNCLY_MESSAGE_TYPE.ANSWER;

            switch (message.type) {
              case HEY_SYNCLY_MESSAGE_TYPE.QUESTION:
                if (props.hideQuestion) {
                  return null;
                }
                return (
                  <QuestionMessage key={message.alias} text={message.text} />
                );
              case HEY_SYNCLY_MESSAGE_TYPE.ANSWER: {
                const isItem = message.item !== undefined;
                if (isItem) {
                  return (
                    <MotionDiv
                      key={message.alias}
                      className={isLastAnswer ? lastAnswerMinHeightClass : ''}
                    >
                      {message.feedback_count && (
                        <AnalyzedFeedbackCountDisplay
                          feedbackCount={message.feedback_count}
                          filter_string={message.filter_string}
                          isItem={isItem}
                        />
                      )}
                      <MessageTextStyle className="p-2 mb-2">
                        Done! Here's your report.
                      </MessageTextStyle>
                      {message.item && (
                        <StudioItemPreview
                          selected={
                            selectedStudioItemDetail === message.item.id
                          }
                          createdAt={message.created_at}
                          item={message.item}
                          onClick={() => {
                            if (!message.item) return;
                            props.onStudioItemClick(message.item);
                            void track(AMP_KEYS.HEY_SYNCLY_STUDIO_ITEM_CLICK, {
                              chat_alias: chat_id,
                              item_alias: message.item.id,
                            });
                          }}
                        />
                      )}
                    </MotionDiv>
                  );
                }
                return (
                  <MotionDiv
                    key={message.alias}
                    className={isLastAnswer ? lastAnswerMinHeightClass : ''}
                  >
                    {message.feedback_count && !props.hideFeedbackCount && (
                      <AnalyzedFeedbackCountDisplay
                        feedbackCount={message.feedback_count}
                        filter_string={message.filter_string}
                        isItem={isItem}
                      />
                    )}
                    {message.reason && !props.hideReason && (
                      <CollapsibleReason
                        className="px-1 my-2"
                        initialOpen={false}
                      >
                        {parse(
                          formatMessageWithCitations({
                            text: message.reason.trim(),
                            message_id: `${message.alias}-reason`, //dummy
                            hideCitation: true,
                          }),
                          parseOptions,
                        )}
                      </CollapsibleReason>
                    )}
                    <MessageTextStyle className="p-2 rounded-3xl">
                      {parse(
                        formatMessageWithCitations({
                          text: message.text,
                          message_id: message.alias,
                        }),
                        parseOptions,
                      )}
                    </MessageTextStyle>

                    <MessageActions
                      prompt={arr[index - 1]?.text || ''}
                      text={message.text}
                      chat_id={chat_id}
                      message_id={message.alias}
                      vote_up={message.vote_up}
                      hideVote={props.hideVote}
                      additionalAction={props.additionalAction}
                    />
                  </MotionDiv>
                );
              }
              default:
                return null;
            }
          })}
          {answer && <TempAnswerDisplay answer={answer} />}
          {/* Spacer */}
          <div className="h-43.5" />
        </div>
      </div>
    </div>
  );
};
